import { useIsVitrine } from '@/lib/use-instance';
import { type RenderLink, Tabs } from '@pms-digital/design-system';
import { Link, useRouterState } from '@tanstack/react-router';

/**
 * The tab bar of Réglages, back after the coupe (§11.5).
 *
 * The coupe (issue #32) removed the bar and the `h1` TOGETHER, because a lone
 * tab is not a navigation: it leads nowhere else and it suggests a sibling that
 * does not exist. A second tab inverts the gesture exactly, which is why this
 * file and the `h1` of `reglages.tsx` land in the same lot and neither makes
 * sense alone.
 *
 * ## Its own list, and not `admin-nav.ts`
 *
 * `ADMIN_RAIL` is the eight destinations an operator learns once, and Réglages
 * is ONE of them. Its sub-tabs are the inside of that destination, so putting
 * them in the same catalogue would make `activeRailId` and `doorForPath` answer
 * for two different kinds of thing. Two short lists that never overlap beat one
 * list with a `kind` field nobody reads.
 *
 * ## The package `Tabs`, in its link form
 *
 * The bar is `Tabs` of `@pms-digital/design-system` with an `href` on every
 * item and the router's `Link` as `renderLink`: 48 px tabs underlined in bleu
 * nuit, the arrows moving the focus and Enter navigating. Each tab is therefore a
 * `role="tab"` anchor and no longer a plain link in a `nav` - the e2e specs that
 * counted `getByRole('link')` inside `reglages-onglets` read tabs now. The tab
 * shown as active is the one `value` names, decided here from the EXACT pathname,
 * never by prefix, which is the rail's rule too; `Tabs` paints it, so no class
 * string is chosen here any more.
 *
 * `Link` still sets `aria-current="page"` itself, by prefix, and that is safe
 * here and only here because no tab's path is a prefix of another's -
 * `/admin/reglages/modeles`, `/admin/reglages/personas`, `/admin/reglages/partage`,
 * `/admin/reglages/regles`, `/admin/reglages/jetons`, `/admin/reglages/noyaux`,
 * `/admin/reglages/methode` and `/admin/reglages/rejeu` are siblings, and issues
 * #59, #62, #84, #148, #196 and #193 each kept that property when they added the
 * third, the fourth, the fifth, the sixth, the seventh and the one that exists
 * only where the replay is open. « noyaux » shares no prefix with the five before
 * it, which is what let it in without the rail's `activeRailId` machinery, « methode »
 * shares none with the six, and « rejeu » shares none with the seven; a NEXT one
 * has to keep the property too, or take the mark over as the rail does. « Personas »
 * and « Partage » share four letters and no path; so do « Règles » and
 * « Réglages », which is the shell above them and not a tab at all, and « Modèles »
 * and « Méthode », whose paths differ at the third letter.
 */

interface ReglagesTab {
  readonly to:
    | '/admin/reglages/modeles'
    | '/admin/reglages/personas'
    | '/admin/reglages/partage'
    | '/admin/reglages/regles'
    | '/admin/reglages/jetons'
    | '/admin/reglages/noyaux'
    | '/admin/reglages/methode'
    | '/admin/reglages/rejeu';
  readonly label: string;
}

/**
 * The seven tabs of every server, in reading order. Modèles first: it is the
 * older setting.
 * Partage third, and that is §11.4 in as many words - « troisième onglet après
 * Modèles et Personas ». Règles fourth, added by issue #62, and Jetons fifth,
 * added by issue #84: neither §11.3 nor §20.2 gives its tab a rank, and the end
 * of the bar is where a tab lands when nothing says otherwise - moving the ones
 * that exist would move an entry an operator has already learnt. Noyaux sixth,
 * added by issue #148, and this one IS ranked: « un sixième onglet, après
 * Modèles, Personas, Partage, Règles et Jetons » (noyau.md §6.3, N22). Méthode
 * seventh, added by issue #196 and ranked too: « un onglet Méthode, après
 * Noyaux » (analyste.md §4.4, K12) - which puts it before Rejeu, whose own rank
 * is « après Noyaux » and which is appended below, where the replay is open.
 */
export const REGLAGES_TABS: readonly ReglagesTab[] = [
  { to: '/admin/reglages/modeles', label: 'Modèles' },
  { to: '/admin/reglages/personas', label: 'Personas' },
  { to: '/admin/reglages/partage', label: 'Partage' },
  { to: '/admin/reglages/regles', label: 'Règles' },
  { to: '/admin/reglages/jetons', label: 'Jetons' },
  { to: '/admin/reglages/noyaux', label: 'Noyaux' },
  { to: '/admin/reglages/methode', label: 'Méthode' },
];

/**
 * The tab added by issue #193 (docs/specs/rejeu.md §10.3): « un septième onglet
 * de Réglages, Rejeu, après Noyaux » - the last of the bar since Méthode (#196)
 * came in between. It is NOT in `REGLAGES_TABS` and that is the point: the seven
 * above are on every server, and this one exists only where the replay is open
 * (§4) - `ReglagesTabs` appends it when told so, and a closed server (the
 * production) never draws it.
 */
export const REPLAY_TAB: ReglagesTab = { to: '/admin/reglages/rejeu', label: 'Rejeu' };

/**
 * The router's link for a tab of the bar: `Tabs` hands the path and the class, `Link`
 * navigates without a reload and marks the current tab `aria-current="page"` itself.
 * The cast is safe: every `href` this file passes is one of the `to` above.
 */
const renderTabLink: RenderLink = ({ href, className, children }) => (
  <Link to={href as ReglagesTab['to']} className={className}>
    {children}
  </Link>
);

export interface ReglagesTabsProps {
  /**
   * The replay is open on this server (`GET /api/replay-settings` did not
   * answer 404): the tab of the replay is drawn, last. Absent or false, the bar is
   * the seven of every server - which is also what the tests that mount the bar
   * bare see.
   */
  readonly replayOpen?: boolean;
}

/**
 * The two tabs the vitrine does not have (docs/specs/vitrine.md section 6, issue
 * #192): Personas - the library of tones and levels, which only serves to WRITE
 * a persona (H1) - and Jetons - without the assistant a personal token serves
 * nothing (H6). Rejeu is the atelier's alone and never drawn there. Méthode (#196)
 * IS drawn: it reads there without a gesture, like Modèles, Règles and Noyaux
 * (vitrine.md section 7).
 */
const VITRINE_WITHOUT: readonly ReglagesTab['to'][] = [
  '/admin/reglages/personas',
  '/admin/reglages/jetons',
];

export function ReglagesTabs({ replayOpen = false }: ReglagesTabsProps = {}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const vitrine = useIsVitrine();
  const all = replayOpen && !vitrine ? [...REGLAGES_TABS, REPLAY_TAB] : REGLAGES_TABS;
  const tabs = vitrine ? all.filter((tab) => !VITRINE_WITHOUT.includes(tab.to)) : all;
  // The mark rides an envelope: `Tabs` takes no `data-testid`.
  return (
    <div data-testid="reglages-onglets">
      <Tabs
        label="Réglages"
        value={pathname}
        items={tabs.map((tab) => ({ value: tab.to, label: tab.label, href: tab.to }))}
        renderLink={renderTabLink}
      />
    </div>
  );
}
